import { useEffect, useState } from "react"
import { capitalizeString } from "src/shared/hooks/capitalizeString"
import { formatCurrency, formatCurrencyToFixed } from "src/shared/hooks/formatCurrency"

import LastRowColumn from "../sections/LastRowColumn/LastRowColumn"
import { FlexibleTableRow } from "src/shared/components/data-display/FlexibleTable/flexible-table-items.interface"
import {
    Cancelaciones,
    GastosCorte,
    IncidenciaCorte,
    Reservas,
    ResumenCorteTickets,
    ResumenCorteTicketsEstancia,
} from "../interfaces/resumenCorteTickets"
import { ResumenPagos } from "../interfaces/resumenPagos"
import { formatTipoPago } from "src/shared/helpers/formatTipoPago"
import CellDetails from "../components/CellDetails/CellDetails"

export function useRows(resumenCorte?: ResumenCorteTickets, resumenPagos?: ResumenPagos) {
    const [rowsEstancia, setRowsEstancia] = useState<FlexibleTableRow[]>([])
    const [rowsRoomService, setRowsRoomService] = useState<FlexibleTableRow[]>([])
    const [rowsRestaurante, setRowsRestaurante] = useState<FlexibleTableRow[]>([])
    const [rowsReservas, setRowsReservas] = useState<FlexibleTableRow[]>([])
    const [rowsEfectivo, setRowsEfectivo] = useState<FlexibleTableRow[]>([])
    const [rowsGastos, setRowsGastos] = useState<FlexibleTableRow[]>([])
    const [rowsIncidencias, setRowsIncidencias] = useState<FlexibleTableRow[]>([])
    const [rowsCancelaciones, setRowsCancelaciones] = useState<FlexibleTableRow[]>([])
    const [rowsPropinas, setRowsPropinas] = useState<FlexibleTableRow[]>([])

    const getRowsEstancia = (data: ResumenCorteTicketsEstancia) => {
        const array: FlexibleTableRow[] = []
        data?.habitaciones?.map((habitaciones) => {
            const hasAnticipos = Number(habitaciones.cantidad_anticipos_validos) > 0
            const validPromedio = Number(habitaciones?.precio_promedio) > 0 ? formatCurrency(Number(habitaciones?.precio_promedio)) : "-"
            const validTotal = Number(habitaciones?.total) > 0 ? formatCurrency(Number(habitaciones?.total)) : "-"

            array.push({
                value: [
                    { value: <CellDetails title={habitaciones?.concepto} description={hasAnticipos && "Anticipo válido"} /> },
                    { value: validPromedio },
                    { value: <CellDetails title={habitaciones?.cantidad} description={hasAnticipos && habitaciones.cantidad_anticipos_validos} details={hasAnticipos && habitaciones.suma_cantidades} /> },
                    { value: <CellDetails title={validTotal} description={hasAnticipos && formatCurrency(Number((habitaciones.total_anticipos_validos)))} details={hasAnticipos && formatCurrency(Number(habitaciones.suma_totales))} /> },
                ],
            })
        })
        data?.extras?.map((extras) => {
            const hasAnticipos = Number(extras.cantidad_anticipos_validos) > 0

            array.push({
                value: [
                    { value: <CellDetails title={extras?.concepto} description={hasAnticipos && "Anticipo válido"} /> },
                    { value: formatCurrency(Number(extras?.precio_promedio)) },
                    { value: <CellDetails title={extras?.cantidad} description={hasAnticipos && extras.cantidad_anticipos_validos} details={hasAnticipos && extras.suma_cantidades} /> },
                    { value: <CellDetails title={formatCurrency(Number(extras?.total))} description={hasAnticipos && formatCurrency(Number((extras.total_anticipos_validos)))} details={hasAnticipos && formatCurrency(Number(extras.suma_totales))} /> },
                ],
            })
        })
        array.push({
            value: [
                { value: "Check-in anticipado" },
                { value: formatCurrency(Number(data?.early_checkin?.[0]?.precio_promedio)) },
                { value: data?.early_checkin?.[0]?.cantidad },
                { value: formatCurrency(Number(data?.early_checkin?.[0]?.total)) },
            ],
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsRoomService = (data: GastosCorte) => {
        const array: FlexibleTableRow[] = []
        data?.desgloce?.map((categoria) => {
            array.push({
                value: [
                    { value: capitalizeString(categoria?.concepto) },
                    { value: categoria?.cantidad },
                    { value: formatCurrency(Number(categoria?.total)) },
                ],
            })
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsRestaurante = (data: GastosCorte) => {
        const array: FlexibleTableRow[] = []
        data?.desgloce?.map((categoria) => {
            array.push({
                value: [
                    { value: capitalizeString(categoria?.concepto) },
                    { value: categoria?.cantidad },
                    { value: formatCurrency(Number(categoria?.total)) },
                ],
            })
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsCancelaciones = (data: Cancelaciones) => {
        const array: FlexibleTableRow[] = []
        if (data) {
            if (Number(data?.estancia?.no_pagadas?.total) > 0 || Number(data?.estancia?.pagadas?.total) > 0) {
                array.push({
                    value: [
                        { value: "Estancia" },
                        { value: Number(data?.estancia?.pagadas?.cantidad || 0) },
                        { value: formatCurrencyToFixed(Number(data?.estancia?.pagadas.total) || 0) },
                        { value: Number(data?.estancia?.no_pagadas?.cantidad) },
                        { value: formatCurrencyToFixed(Number(data?.estancia?.no_pagadas.total) || 0) },
                    ],
                })
            }
            if (Number(data?.roomservice?.no_pagadas?.total) > 0 || Number(data?.roomservice?.pagadas?.total) > 0) {
                array.push({
                    value: [
                        { value: "Room service" },
                        { value: data?.roomservice?.pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.roomservice?.pagadas?.total) || 0) },
                        { value: data?.roomservice?.no_pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.roomservice?.no_pagadas?.total) || 0) },
                    ],
                })
            }
            if (Number(data?.reservas?.no_pagadas?.total) > 0 || Number(data?.reservas?.pagadas?.total) > 0) {
                array.push({
                    value: [
                        { value: "Reservas" },
                        { value: data?.reservas?.pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.reservas?.pagadas?.total) || 0) },
                        { value: data?.reservas?.no_pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.reservas?.no_pagadas?.total) || 0) },
                    ],
                })
            }
            if (Number(data?.restaurante?.no_pagadas?.total) > 0 || Number(data?.restaurante?.pagadas?.total) > 0) {
                array.push({
                    value: [
                        { value: "Restaurante" },
                        { value: data?.restaurante?.pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.restaurante?.pagadas?.total) || 0) },
                        { value: data?.restaurante?.no_pagadas?.cantidad },
                        { value: formatCurrencyToFixed(Number(data?.restaurante?.no_pagadas?.total) || 0) },
                    ],
                })
            }
            array.push({
                value: [
                    {
                        value: <LastRowColumn text="Total" bold />,
                    },
                    {
                        value: <LastRowColumn text={data?.totales?.pagadas?.cantidad_total || 0} />,
                    },
                    {
                        value: <LastRowColumn text={formatCurrencyToFixed(Number(data?.totales?.pagadas?.monto_total || 0))} />,
                    },
                    {
                        value: <LastRowColumn text={data?.totales?.no_pagadas?.cantidad_total || 0} />,
                    },
                    {
                        value: <LastRowColumn text={formatCurrency(Number(data?.totales?.no_pagadas?.monto_total || 0))} />,
                    },
                ],
                className: "last-row__resumen-turno",
            })
        }

        return array
    }

    const getRowsReservas = (data: Reservas) => {
        const array: FlexibleTableRow[] = []
        if (!data?.anticipos_reserva?.length || !data?.saldos_reservas?.length) {
            return array
        }
        data?.saldos_reservas?.map((saldos) => {
            array.push({
                value: [
                    { value: saldos?.concepto },
                    { value: saldos?.cantidad },
                    { value: formatCurrency(Number(saldos?.total)) },
                ],
            })
        })
        data?.anticipos_reserva?.map((anticipos) => {
            array.push({
                value: [
                    { value: anticipos?.concepto },
                    { value: anticipos?.cantidad },
                    { value: formatCurrency(Number(anticipos?.total)) },
                ],
            })
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsEfectivo = (data: ResumenPagos) => {
        const array: FlexibleTableRow[] = []
        data.fajillas.desgloce?.map((fajilla) => {
            array.push({
                value: [
                    { value: "Retiros de efectivo" },
                    { value: fajilla?.cantidad },
                    { value: formatCurrency(Number(fajilla?.monto)) },
                    { value: formatCurrency(Number(fajilla?.total)) },
                ],
            })
        })
        array.push({
            value: [
                { value: "Efectivo en caja" },
                { value: "" },
                { value: formatCurrency(Number(data?.efectivo_disponible_recepcion || 0)) },
                {
                    value: formatCurrency(Number(data?.efectivo_disponible_recepcion || 0)),
                },
            ],
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text={""} />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.efectivo_menos_gastos || 0))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsPropinas = (data: GastosCorte) => {
        const array: FlexibleTableRow[] = []
        if (!data?.desgloce?.length) {
            return array
        }
        data?.desgloce?.map((desgloce) => {
            array.push({
                value: [
                    { value: desgloce?.concepto ? formatTipoPago(desgloce?.concepto) : "Otro"},
                    { value: desgloce?.cantidad },
                    { value: formatCurrency(Number(desgloce?.total)) },
                ],
            })
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text="" />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsGastos = (data: GastosCorte) => {
        const array: FlexibleTableRow[] = []
        if (!data?.desgloce?.length) {
            return array
        }
        data?.desgloce?.map((gastos) => {
            array.push({
                value: [{ value: gastos?.concepto }, { value: formatCurrency(Number(gastos?.total)) }],
            })
        })
        array.push({
            value: [
                {
                    value: <LastRowColumn text="Total" bold />,
                },
                {
                    value: <LastRowColumn text={formatCurrency(Number(data?.totales?.monto_total))} />,
                },
            ],
            className: "last-row__resumen-turno",
        })
        return array
    }

    const getRowsIncidencias = (data: IncidenciaCorte[]) => {
        const array: FlexibleTableRow[] = []
        data?.map((incidencias: IncidenciaCorte) => {
            array.push({
                value: [
                    { value: incidencias?.folio },
                    { value: incidencias?.estado === "activa" ? "Abierta" : "Cerrada" },
                    { value: incidencias?.detalle },
                    { value: capitalizeString(incidencias?.severidad) },
                    { value: capitalizeString(incidencias?.area) },
                    { value: incidencias?.numero_habitacion || "N/A" },
                ],
            })
        })

        return array
    }

    useEffect(() => {
        if (resumenCorte && resumenPagos) {
            setRowsEstancia(getRowsEstancia(resumenCorte?.estancia))
            setRowsRoomService(getRowsRoomService(resumenCorte?.room_service))
            setRowsRestaurante(getRowsRestaurante(resumenCorte?.restaurante))
            setRowsReservas(getRowsReservas(resumenCorte?.reservas))
            setRowsEfectivo(getRowsEfectivo(resumenPagos))
            setRowsGastos(getRowsGastos(resumenCorte?.gastos_corte))
            setRowsIncidencias(getRowsIncidencias(resumenCorte?.incidencias_corte))
            setRowsCancelaciones(getRowsCancelaciones(resumenCorte?.cancelaciones))
            setRowsPropinas(getRowsPropinas(resumenCorte?.propinas_corte))
        } else {
            setRowsRoomService([])
            setRowsRestaurante([])
            setRowsEstancia([])
            setRowsReservas([])
            setRowsEfectivo([])
            setRowsGastos([])
            setRowsIncidencias([])
            setRowsCancelaciones([])
        }
    }, [resumenCorte, resumenPagos])

    return {
        rowsEstancia,
        rowsRoomService,
        rowsRestaurante,
        rowsReservas,
        rowsEfectivo,
        rowsGastos,
        rowsIncidencias,
        rowsCancelaciones,
        rowsPropinas,
    }
}
